import type { Trend } from './types';

export function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

export function mean(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((acc, value) => acc + value, 0) / values.length;
}

/** Gleitender Mittelwert in der Leistungsdomäne (τ in Sekunden), typisch für Lautstärke-Anzeigen. */
export function smoothLevelDbEma(previousDb: number, sampleDb: number, frameDurationSec: number, tauSec: number): number {
  if (tauSec <= 0 || frameDurationSec <= 0) return sampleDb;
  const alpha = 1 - Math.exp(-frameDurationSec / tauSec);
  const previousPower = 10 ** (previousDb / 10);
  const samplePower = 10 ** (sampleDb / 10);
  const blendedPower = previousPower + alpha * (samplePower - previousPower);
  return 10 * Math.log10(Math.max(blendedPower, 1e-12));
}

export function variance(values: number[]): number {
  if (values.length <= 1) return 0;
  const avg = mean(values);
  return values.reduce((acc, value) => acc + (value - avg) ** 2, 0) / values.length;
}

export function standardDeviation(values: number[]): number {
  return Math.sqrt(variance(values));
}

export function slope(values: number[]): number {
  if (values.length < 2) return 0;
  return (values[values.length - 1] - values[0]) / (values.length - 1);
}

export function getTrend(value: number, epsilon = 0.008): Trend {
  if (value > epsilon) return 'rising';
  if (value < -epsilon) return 'falling';
  return 'stable';
}

export function toBinIndex(freqHz: number, sampleRate: number, frequencyBinCount: number): number {
  if (!sampleRate || !frequencyBinCount) return 0;
  const nyquist = sampleRate / 2;
  const normalized = Math.max(0, Math.min(1, freqHz / nyquist));
  return Math.max(0, Math.min(frequencyBinCount - 1, Math.floor(normalized * (frequencyBinCount - 1))));
}

export function correlationAtLag(series: number[], lag: number): number {
  if (series.length <= lag + 2) return 0;
  const left = series.slice(0, series.length - lag);
  const right = series.slice(lag);
  const leftMean = mean(left);
  const rightMean = mean(right);
  let numerator = 0;
  let leftVariance = 0;
  let rightVariance = 0;
  for (let i = 0; i < left.length; i += 1) {
    const a = left[i] - leftMean;
    const b = right[i] - rightMean;
    numerator += a * b;
    leftVariance += a * a;
    rightVariance += b * b;
  }
  const denominator = Math.sqrt(leftVariance * rightVariance);
  if (denominator <= 1e-9) return 0;
  return numerator / denominator;
}

export function autocorrelation(series: number[], minLag = 2, maxLag = 10): number {
  if (series.length < minLag + 3) return 0;
  let best = 0;
  for (let lag = minLag; lag <= maxLag; lag += 1) {
    best = Math.max(best, correlationAtLag(series, lag));
  }
  return clamp01((best + 1) / 2);
}

export function normalizedEntropy(values: number[]): number {
  if (!values.length) return 0;
  const positive = values.map((value) => Math.max(0, value));
  const sum = positive.reduce((acc, value) => acc + value, 0);
  if (sum <= 1e-12) return 0;
  const probabilities = positive.map((value) => value / sum);
  const entropy = probabilities.reduce((acc, p) => (p > 0 ? acc - p * Math.log2(p) : acc), 0);
  return clamp01(entropy / Math.log2(probabilities.length));
}

export function pushLimited<T>(list: T[], value: T, limit: number): T[] {
  const next = [...list, value];
  if (next.length <= limit) return next;
  return next.slice(next.length - limit);
}
